import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	ViewChild
} from '@angular/core';
import { BarChartDataItem } from '@app-model/dashboard';
import { v4 as uuidv4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { Chart, registerables, LayoutPosition } from 'chart.js';

import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables);
Chart.register(ChartDataLabels);

@Component({
	selector: 'app-bar-chart',
	templateUrl: './bar-chart.component.html',
	styleUrls: ['./bar-chart.component.scss']
})
export class BarChartComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('chartCanvas')
	private readonly chartCanvas?: ElementRef<HTMLCanvasElement>;

	@Input() labels: string[];
	@Input() data: BarChartDataItem[];
	@Input() dataInfo: any[];
	@Input() typeChart: any = 'line';
	@Input() positionChart: LayoutPosition = 'right';
	@Input() statType: string;

	private decimalPipe: DecimalPipe;
	height: number;
	uuid: string;
	private readonly isBrowser: boolean;
	private chart?: Chart;

	constructor(public translate: TranslateService, @Inject(PLATFORM_ID) platformId: object) {
		this.uuid = uuidv4();
		this.isBrowser = isPlatformBrowser(platformId);
	}

	ngOnInit(): void {
		this.decimalPipe = new DecimalPipe(this.translate.currentLang);
	}

	ngAfterViewInit(): void {
		if (!this.isBrowser) {
			return;
		}

		if (this.data !== undefined && this.labels !== undefined) {
			this.handleInitChartjs();
		}
	}

	ngOnDestroy(): void {
		this.chart?.destroy();
	}

	private handleInitChartjs(): void {
		if (!this.isBrowser) {
			return;
		}
		const horizontal = this.data.length > 10;
		if (horizontal) {
			this.height = this.data.length * 32 + 48;
		}

		const canvasEl = this.chartCanvas?.nativeElement;
		if (!canvasEl) {
			return;
		}

		const customColors = [
			'#054659',
			'#F18700',
			'#2A77AD',
			'#F9B000',
			'#3BB93B',
			'#7460DD',
			'#0099C9',
			'#F0BE4B',
			'#61CE61',
			'#20829E',
			'#9487D3',
			'#FFAC41',
			'#50BEDF',
			'#B9DFB9',
			'#6952D9',
			'#0DA9D9',
			'#F29E2F',
			'#1CAE94',
			'#B47F03',
			'#3494D9',
			'#503FA6',
			'#73D973',
			'#D97B00',
			'#EBA70A',
			'#EBC70A'
		];

		const title = tooltipItems => {
			const dataClients = this.data.map(item => item?.client);
			const dataGroups = this.data.map(item => item?.group);
			const title = tooltipItems[0].label;
			const subTitle =
				dataGroups[tooltipItems[0].dataIndex] !== undefined
					? dataClients[tooltipItems[0].dataIndex] + ' | ' + dataGroups[tooltipItems[0].dataIndex]
					: dataClients[tooltipItems[0].dataIndex];

			return [title, subTitle];
		};

		try {
			this.chart?.destroy();
			this.chart = new Chart(canvasEl, {
				type: 'bar',
				data: {
					labels: this.labels,
					datasets: [
						{
							label: this.translate.instant(
								'stats.' + (this.statType === 'PAGEVIEW' ? 'views' : 'clicks').toLowerCase()
							),
							data: this.data.map(item => item.value),
							backgroundColor: customColors,
							borderRadius: 4,
							barThickness: horizontal ? 24 : undefined
						}
					]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					indexAxis: horizontal ? 'y' : 'x',

					plugins: {
						tooltip: {
							callbacks: {
								title: title,
								labelTextColor: function () {
									return '#FFFFFF';
								},
								label: function (context) {
									const total = context.dataset.data.reduce(
										(accumulator: number, currentValue: number) => accumulator + currentValue,
										0
									);
									const totalNumber = Array.isArray(total) ? total[0] : total;
									const currentRaw = Number(context.raw ?? 0);
									const percent = totalNumber === 0 ? 0 : ((currentRaw / totalNumber) * 100).toFixed(0);
									return ' ' + context.raw + ' ' + context.dataset.label + ' (' + `${percent}%` + ')';
								}
							},
							bodyFont: {
								size: 12
							},
							position: 'nearest',
							titleColor: '#FFFFFF',
							backgroundColor: '#262626',
							displayColors: true
						},
						legend: {
							display: false,
							position: 'right',
							labels: {
								padding: 20,
								font: {
									size: 12,
									weight: 400,
									lineHeight: 22,
									family: 'ZT Nature'
								},
								usePointStyle: true,
								boxHeight: 7,
								pointStyleWidth: 10
							}
						},
						datalabels: {
							color: '#ffffff',
							anchor: horizontal ? 'center' : 'end',
							align: horizontal ? 'center' : 'bottom',
							clamp: true,
							clip: true,
							font: {
								weight: 'bold'
							},
							formatter: value => {
								if (value) {
									return this.decimalPipe.transform(value.toString());
								}
								return value;
							}
						}
					},
					scales: {
						x: {
							beginAtZero: true,
							display: horizontal ? undefined : true,
							grid: {
								display: horizontal
							},
							border: {
								display: false
							},
							ticks: {
								maxTicksLimit: horizontal ? 5 : undefined
							}
						},
						y: {
							beginAtZero: true,
							display: horizontal ? true : undefined,
							title: {
								display: horizontal ? undefined : true
							},
							grid: {
								display: !horizontal
							},
							border: {
								display: false
							},
							ticks: {
								maxTicksLimit: horizontal ? undefined : 5
							}
						}
					}
				}
			});
		} catch (error) {
			console.warn('Skipping bar chart render (SSR/DOM not supported).', error);
			this.chart?.destroy();
			this.chart = undefined;
		}
	}
}
